import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate, useParams } from 'react-router'

import { useNotifications } from 'app/application/application'
import { ConnectionSetupRoutes } from 'app/application/types'
import { combineClassnames } from 'app/common/presentation'
import { useConnectionsApi } from 'app/connections/application'
import { SupportedAccount } from 'app/connections/types'

import { ActivityIndicator, Button } from 'components'

import { mapToConnectionName } from '../../connectionUrlMapper'
import Accounts from '../Accounts'
import { useConnection } from '../Connection'
import setupStyles from '../Setup.module.scss'
import styles from './Steps.module.scss'

type SelectAccountsParams = {
  name: string
}

const singleSelectionConnections = ['googleAnalytics']

const SelectAccounts = () => {
  const navigate = useNavigate()
  const { createNotification, te } = useNotifications()
  const { name } = useParams<SelectAccountsParams>() as SelectAccountsParams
  const { t } = useTranslation('connections')

  const [availableAccounts, setAvailableAccounts] = useState<SupportedAccount[]>([])
  const [selectedAccounts, setSelectedAccounts] = useState<SupportedAccount[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const connectionName = mapToConnectionName(name)

  const connection = useConnection()
  const { getAccounts } = useConnectionsApi(connectionName)

  const isSingleSelection = singleSelectionConnections.includes(connection.name)

  useEffect(
    () =>
      void getAccounts()
        .then((result) => {
          if (result.ok) {
            setAvailableAccounts(result.val)
          } else {
            createNotification({
              message: result.val.message,
              title: te(`http.${result.val.code}`),
              type: 'error',
            })
          }
        })
        .finally(() => setIsLoading(false)),
    [createNotification, getAccounts, name, te],
  )

  if (!connection || connection.name !== connectionName) {
    return <Navigate to={`/connections/${name}/setup`} replace />
  }

  const handleAccountClick = (account: SupportedAccount) =>
    setSelectedAccounts((prev) =>
      isSingleSelection
        ? prev.includes(account)
          ? []
          : [account]
        : prev.includes(account)
          ? prev.filter((a) => a.id !== account.id)
          : [...prev, account],
    )

  return (
    <div className={combineClassnames(styles.step, styles.accounts)}>
      <div className={setupStyles.form}>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <ActivityIndicator size="l" />
          </div>
        ) : (
          <Accounts
            accounts={availableAccounts}
            selectionOptions={{
              isSelected: (account) => selectedAccounts.includes(account),
              onClick: handleAccountClick,
            }}
            connectionName={connection.name}
          />
        )}
      </div>

      <div className={setupStyles.actions}>
        <Button onClick={() => void navigate(-1)} variant="secondary">
          {t('setup.actions.back')}
        </Button>

        <Button
          disabled={!selectedAccounts.length}
          onClick={(e) => {
            e.stopPropagation()
            void navigate(`../${ConnectionSetupRoutes.ConnectionVerified}`, {
              state: { accounts: selectedAccounts, connection: connection },
            })
          }}
        >
          {t('setup.actions.next')}
        </Button>
      </div>
    </div>
  )
}

export default SelectAccounts
